import type {
  ApprovedFormulaDefinition,
  ApprovedFormulaResult,
} from './approved-formulas';
import { CAPITAL_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF } from './approved-source-systems';

export { CAPITAL_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF } from './approved-source-systems';

export const CAPITAL_ALLOCATION_FORMULA_CONTEXT_KIND =
  'decision_agent:capital_allocation:governed_snapshot';

type CapitalAllocationProject = Readonly<{
  projectId: string;
  approvedCapexUsd: number;
  expectedReturnUsd?: number;
  actualCapexUsd?: number;
  nopatUsd?: number;
  investedCapitalUsd?: number;
  maintenanceGrowthTag?: 'maintenance' | 'growth';
  assetLifeYears?: number;
  cashflowsUsd?: readonly number[];
}>;

type FormulaDefinitionInput<TInput, TOutput> = Readonly<{
  formulaId: string;
  displayName: string;
  description: string;
  inputSchemaVersion: string;
  outputSchemaVersion: string;
  execute(input: TInput): ApprovedFormulaResult<TOutput>;
  goldenTraces: readonly Readonly<{
    traceId: string;
    input: TInput;
    expected: ApprovedFormulaResult<TOutput>;
  }>[];
}>;

function definition<TInput, TOutput>(
  input: FormulaDefinitionInput<TInput, TOutput>,
): ApprovedFormulaDefinition<TInput, TOutput> {
  return Object.freeze({
    formulaId: input.formulaId,
    version: '1.0.0',
    displayName: input.displayName,
    description: input.description,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: input.inputSchemaVersion,
    outputSchemaVersion: input.outputSchemaVersion,
    expectedContextKind: CAPITAL_ALLOCATION_FORMULA_CONTEXT_KIND,
    approvedSourceSystemRefs: Object.freeze([
      CAPITAL_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    ]),
    implementationDigestInputs: Object.freeze([
      Object.freeze({
        name: input.formulaId,
        source: 'packages/lib/src/capital-allocation-formulas.ts',
      }),
    ]),
    goldenTraces: Object.freeze(input.goldenTraces),
    execute: input.execute,
  });
}

function blocked<T>(message: string): ApprovedFormulaResult<T> {
  return {
    status: 'blocked',
    reasonCode: 'invalid_formula_input',
    message,
  };
}

function missing<T>(message: string): ApprovedFormulaResult<T> {
  return {
    status: 'blocked',
    reasonCode: 'missing_formula_input',
    message,
  };
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function round(value: number, decimals = 6): number {
  const factor = 10 ** decimals;
  const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function readProjects(value: unknown): CapitalAllocationProject[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const ids = new Set<string>();
  const projects: CapitalAllocationProject[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') return null;
    const record = item as Record<string, unknown>;
    const projectId = typeof record.projectId === 'string' ? record.projectId.trim() : '';
    if (!projectId || ids.has(projectId) || !isFiniteNumber(record.approvedCapexUsd)) {
      return null;
    }
    if (record.approvedCapexUsd < 0) return null;
    ids.add(projectId);
    projects.push({
      projectId,
      approvedCapexUsd: record.approvedCapexUsd,
      expectedReturnUsd: numberOrUndefined(record.expectedReturnUsd),
      actualCapexUsd: numberOrUndefined(record.actualCapexUsd),
      nopatUsd: numberOrUndefined(record.nopatUsd),
      investedCapitalUsd: numberOrUndefined(record.investedCapitalUsd),
      maintenanceGrowthTag:
        record.maintenanceGrowthTag === 'maintenance' || record.maintenanceGrowthTag === 'growth'
          ? record.maintenanceGrowthTag
          : undefined,
      assetLifeYears: numberOrUndefined(record.assetLifeYears),
      cashflowsUsd: readCashflows(record.cashflowsUsd),
    });
  }
  return projects;
}

function numberOrUndefined(value: unknown): number | undefined {
  return isFiniteNumber(value) ? value : undefined;
}

function readCashflows(value: unknown): readonly number[] | undefined {
  if (!Array.isArray(value) || value.length < 2 || !value.every(isFiniteNumber)) {
    return undefined;
  }
  return Object.freeze([...value]);
}

function percentageFraction(value: unknown): number | null {
  if (!isFiniteNumber(value) || value < 0 || value > 100) return null;
  return value / 100;
}

function netPresentValue(cashflows: readonly number[], hurdleFraction: number): number {
  return cashflows.reduce(
    (sum, cashflow, periodIndex) => sum + cashflow / (1 + hurdleFraction) ** periodIndex,
    0,
  );
}

function signChangeCount(cashflows: readonly number[]): number {
  let changes = 0;
  let prior = 0;
  for (const cashflow of cashflows) {
    const sign = cashflow === 0 ? 0 : cashflow > 0 ? 1 : -1;
    if (sign !== 0 && prior !== 0 && sign !== prior) changes += 1;
    if (sign !== 0) prior = sign;
  }
  return changes;
}

function solveIrr(cashflows: readonly number[]): number | null {
  if (signChangeCount(cashflows) !== 1) return null;
  let low = -0.9999;
  let high = 1;
  let lowValue = netPresentValue(cashflows, low);
  let highValue = netPresentValue(cashflows, high);
  for (let index = 0; index < 30 && lowValue * highValue > 0; index += 1) {
    high *= 2;
    highValue = netPresentValue(cashflows, high);
  }
  if (lowValue * highValue > 0) return null;
  for (let index = 0; index < 100; index += 1) {
    const midpoint = (low + high) / 2;
    const midpointValue = netPresentValue(cashflows, midpoint);
    if (Math.abs(midpointValue) < 0.00000001) return midpoint;
    if (lowValue * midpointValue <= 0) {
      high = midpoint;
      highValue = midpointValue;
    } else {
      low = midpoint;
      lowValue = midpointValue;
    }
  }
  return (low + high) / 2;
}

function modifiedIrr(
  cashflows: readonly number[],
  financeRate: number,
  reinvestmentRate: number,
): number | null {
  const periods = cashflows.length - 1;
  let pvNegative = 0;
  let fvPositive = 0;
  for (let index = 0; index < cashflows.length; index += 1) {
    const cashflow = cashflows[index];
    if (cashflow < 0) pvNegative += cashflow / (1 + financeRate) ** index;
    if (cashflow > 0) fvPositive += cashflow * (1 + reinvestmentRate) ** (periods - index);
  }
  if (pvNegative >= 0 || fvPositive <= 0 || periods <= 0) return null;
  return (fvPositive / Math.abs(pvNegative)) ** (1 / periods) - 1;
}

type PortfolioRollupInput = Readonly<{
  projects: readonly CapitalAllocationProject[];
  capitalExpendituresUsd: number;
  untaggedMaintenanceCapexUsd?: number;
}>;
type PortfolioRollupOutput = Readonly<{
  totalProjectCapexUsd: number;
  totalExpectedReturnUsd: number;
  capitalExpendituresUsd: number;
  residualUsd: number;
  untaggedMaintenanceCapexUsd: number;
}>;

function executePortfolioRollup(input: PortfolioRollupInput): ApprovedFormulaResult<PortfolioRollupOutput> {
  const projects = readProjects(input?.projects);
  if (!projects || !isFiniteNumber(input?.capitalExpendituresUsd)) {
    return missing('projects and capitalExpendituresUsd are required.');
  }
  if (input.capitalExpendituresUsd < 0) return blocked('capitalExpendituresUsd must be non-negative.');
  const untagged = input.untaggedMaintenanceCapexUsd ?? 0;
  if (!isFiniteNumber(untagged) || untagged < 0) {
    return blocked('untaggedMaintenanceCapexUsd must be non-negative when supplied.');
  }
  const totalProjectCapexUsd = round(
    projects.reduce((sum, project) => sum + project.approvedCapexUsd, 0),
  );
  const residualUsd = round(totalProjectCapexUsd - input.capitalExpendituresUsd);
  if (Math.abs(residualUsd - untagged) > 0.005) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Project-capex total must tie to the delegated capital-cash filing line or name the untagged maintenance residual.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalProjectCapexUsd,
      totalExpectedReturnUsd: round(
        projects.reduce((sum, project) => sum + (project.expectedReturnUsd ?? 0), 0),
      ),
      capitalExpendituresUsd: input.capitalExpendituresUsd,
      residualUsd,
      untaggedMaintenanceCapexUsd: untagged,
    }),
  };
}

type RoicInput = Readonly<{ projects: readonly CapitalAllocationProject[]; hurdlePct: number }>;
type RoicOutput = Readonly<{
  projects: readonly Readonly<{
    projectId: string;
    roicPct: number;
    hurdlePct: number;
    spreadPct: number;
    evaUsd: number;
  }>[];
}>;

function executeRoic(input: RoicInput): ApprovedFormulaResult<RoicOutput> {
  const projects = readProjects(input?.projects);
  const hurdleFraction = percentageFraction(input?.hurdlePct);
  if (!projects || hurdleFraction === null) return missing('projects and a 0-100 hurdlePct are required.');
  const rows = [] as RoicOutput['projects'][number][];
  for (const project of projects) {
    if (!isFiniteNumber(project.nopatUsd) || !isFiniteNumber(project.investedCapitalUsd)) {
      return missing('Each project requires nopatUsd and investedCapitalUsd.');
    }
    if (project.investedCapitalUsd <= 0) {
      return blocked('investedCapitalUsd must be greater than zero for ROIC.');
    }
    const roicFraction = project.nopatUsd / project.investedCapitalUsd;
    rows.push(
      Object.freeze({
        projectId: project.projectId,
        roicPct: round(roicFraction * 100),
        hurdlePct: input.hurdlePct,
        spreadPct: round((roicFraction - hurdleFraction) * 100),
        evaUsd: round((roicFraction - hurdleFraction) * project.investedCapitalUsd),
      }),
    );
  }
  return { status: 'ok', output: Object.freeze({ projects: Object.freeze(rows) }) };
}

type NpvIrrInput = Readonly<{
  projects: readonly CapitalAllocationProject[];
  hurdlePct: number;
  financeRatePct?: number;
  reinvestmentRatePct?: number;
}>;
type NpvIrrOutput = Readonly<{
  projects: readonly Readonly<{
    projectId: string;
    npvUsd: number;
    irrPct: number | null;
    mirrPct: number | null;
    nonConventionalCashflow: boolean;
  }>[];
}>;

function executeNpvIrr(input: NpvIrrInput): ApprovedFormulaResult<NpvIrrOutput> {
  const projects = readProjects(input?.projects);
  const hurdle = percentageFraction(input?.hurdlePct);
  const financeRate = percentageFraction(input?.financeRatePct ?? input?.hurdlePct);
  const reinvestmentRate = percentageFraction(input?.reinvestmentRatePct ?? input?.hurdlePct);
  if (!projects || hurdle === null || financeRate === null || reinvestmentRate === null) {
    return missing('projects and 0-100 hurdlePct, financeRatePct, and reinvestmentRatePct are required.');
  }
  const rows = [] as NpvIrrOutput['projects'][number][];
  for (const project of projects) {
    const cashflows = project.cashflowsUsd;
    if (!cashflows || cashflows[0] >= 0) {
      return blocked('Each project requires a cashflow series with a negative t=0 outlay.');
    }
    const nonConventionalCashflow = signChangeCount(cashflows) > 1;
    const irr = nonConventionalCashflow ? null : solveIrr(cashflows);
    const mirr = modifiedIrr(cashflows, financeRate, reinvestmentRate);
    rows.push(
      Object.freeze({
        projectId: project.projectId,
        npvUsd: round(netPresentValue(cashflows, hurdle)),
        irrPct: irr === null ? null : round(irr * 100),
        mirrPct: mirr === null ? null : round(mirr * 100),
        nonConventionalCashflow,
      }),
    );
  }
  return {
    status: 'ok',
    output: Object.freeze({
      projects: Object.freeze([...rows].sort((left, right) => right.npvUsd - left.npvUsd)),
    }),
  };
}

type ProfitabilityIndexInput = Readonly<{ projects: readonly CapitalAllocationProject[]; hurdlePct: number }>;
type ProfitabilityIndexOutput = Readonly<{
  projects: readonly Readonly<{ projectId: string; npvUsd: number; profitabilityIndex: number }>[];
}>;

function executeProfitabilityIndex(
  input: ProfitabilityIndexInput,
): ApprovedFormulaResult<ProfitabilityIndexOutput> {
  const projects = readProjects(input?.projects);
  const hurdle = percentageFraction(input?.hurdlePct);
  if (!projects || hurdle === null) return missing('projects and a 0-100 hurdlePct are required.');
  const rows = [] as ProfitabilityIndexOutput['projects'][number][];
  for (const project of projects) {
    const cashflows = project.cashflowsUsd;
    if (!cashflows || cashflows[0] >= 0) return blocked('Each project requires a negative t=0 cashflow.');
    const initialInvestmentUsd = Math.abs(cashflows[0]);
    if (initialInvestmentUsd <= 0) return blocked('Initial investment must be greater than zero for profitability index.');
    const npvUsd = netPresentValue(cashflows, hurdle);
    rows.push(
      Object.freeze({
        projectId: project.projectId,
        npvUsd: round(npvUsd),
        profitabilityIndex: round(1 + npvUsd / initialInvestmentUsd),
      }),
    );
  }
  return {
    status: 'ok',
    output: Object.freeze({
      projects: Object.freeze([...rows].sort((left, right) => right.profitabilityIndex - left.profitabilityIndex)),
    }),
  };
}

type PaybackInput = Readonly<{ projects: readonly CapitalAllocationProject[]; hurdlePct: number }>;
type PaybackOutput = Readonly<{
  projects: readonly Readonly<{ projectId: string; paybackYears: number | null; discountedPaybackYears: number | null }>[];
}>;

function paybackPeriod(cashflows: readonly number[], hurdle: number): number | null {
  let cumulative = 0;
  for (let index = 0; index < cashflows.length; index += 1) {
    const cashflow = cashflows[index] / (1 + hurdle) ** index;
    const previous = cumulative;
    cumulative += cashflow;
    if (cumulative >= 0 && index > 0) {
      return round(index - cumulative / cashflow);
    }
    if (previous === 0 && cumulative === 0) continue;
  }
  return null;
}

function executePayback(input: PaybackInput): ApprovedFormulaResult<PaybackOutput> {
  const projects = readProjects(input?.projects);
  const hurdle = percentageFraction(input?.hurdlePct);
  if (!projects || hurdle === null) return missing('projects and a 0-100 hurdlePct are required.');
  const rows = [] as PaybackOutput['projects'][number][];
  for (const project of projects) {
    const cashflows = project.cashflowsUsd;
    if (!cashflows || cashflows[0] >= 0) return blocked('Each project requires a negative t=0 cashflow.');
    rows.push(
      Object.freeze({
        projectId: project.projectId,
        paybackYears: paybackPeriod(cashflows, 0),
        discountedPaybackYears: paybackPeriod(cashflows, hurdle),
      }),
    );
  }
  return { status: 'ok', output: Object.freeze({ projects: Object.freeze(rows) }) };
}

type MixInput = Readonly<{ projects: readonly CapitalAllocationProject[]; revenueUsd: number; maintenanceTargetMinPct: number; maintenanceTargetMaxPct: number }>;
type MixOutput = Readonly<{
  totalCapexUsd: number;
  maintenanceCapexUsd: number;
  growthCapexUsd: number;
  maintenanceRatioPct: number;
  capexIntensityPct: number;
  withinTargetBand: boolean;
}>;

function executeMix(input: MixInput): ApprovedFormulaResult<MixOutput> {
  const projects = readProjects(input?.projects);
  if (!projects || !isFiniteNumber(input?.revenueUsd) || input.revenueUsd <= 0) {
    return missing('projects and positive revenueUsd are required.');
  }
  if (!isFiniteNumber(input.maintenanceTargetMinPct) || !isFiniteNumber(input.maintenanceTargetMaxPct)) {
    return missing('maintenance target band is required.');
  }
  if (input.maintenanceTargetMinPct < 0 || input.maintenanceTargetMaxPct > 100 || input.maintenanceTargetMinPct > input.maintenanceTargetMaxPct) {
    return blocked('maintenance target band must satisfy 0 <= min <= max <= 100.');
  }
  if (projects.some((project) => !project.maintenanceGrowthTag)) {
    return missing('Each project requires maintenanceGrowthTag.');
  }
  const totalCapexUsd = projects.reduce((sum, project) => sum + project.approvedCapexUsd, 0);
  if (totalCapexUsd <= 0) return blocked('Total project capex must be greater than zero.');
  const maintenanceCapexUsd = projects
    .filter((project) => project.maintenanceGrowthTag === 'maintenance')
    .reduce((sum, project) => sum + project.approvedCapexUsd, 0);
  const maintenanceRatioPct = (maintenanceCapexUsd / totalCapexUsd) * 100;
  return {
    status: 'ok',
    output: Object.freeze({
      totalCapexUsd: round(totalCapexUsd),
      maintenanceCapexUsd: round(maintenanceCapexUsd),
      growthCapexUsd: round(totalCapexUsd - maintenanceCapexUsd),
      maintenanceRatioPct: round(maintenanceRatioPct),
      capexIntensityPct: round((totalCapexUsd / input.revenueUsd) * 100),
      withinTargetBand:
        maintenanceRatioPct >= input.maintenanceTargetMinPct &&
        maintenanceRatioPct <= input.maintenanceTargetMaxPct,
    }),
  };
}

type VarianceInput = Readonly<{ projects: readonly CapitalAllocationProject[] }>;
type VarianceOutput = Readonly<{
  projects: readonly Readonly<{ projectId: string; varianceUsd: number; overrunPct: number }>[];
}>;

function executeVariance(input: VarianceInput): ApprovedFormulaResult<VarianceOutput> {
  const projects = readProjects(input?.projects);
  if (!projects) return missing('projects are required.');
  const rows = [] as VarianceOutput['projects'][number][];
  for (const project of projects) {
    if (!isFiniteNumber(project.actualCapexUsd) || project.approvedCapexUsd <= 0) {
      return missing('Each project requires positive approvedCapexUsd and actualCapexUsd.');
    }
    const varianceUsd = project.actualCapexUsd - project.approvedCapexUsd;
    rows.push(Object.freeze({
      projectId: project.projectId,
      varianceUsd: round(varianceUsd),
      overrunPct: round((varianceUsd / project.approvedCapexUsd) * 100),
    }));
  }
  return { status: 'ok', output: Object.freeze({ projects: Object.freeze(rows) }) };
}

type ReprioritizationValueInput = Readonly<{
  fundedNpvUsd: readonly number[];
  displacedNpvUsd: readonly number[];
}>;
type ReprioritizationValueOutput = Readonly<{ capitalFreedUsd: number }>;

function executeReprioritizationValue(
  input: ReprioritizationValueInput,
): ApprovedFormulaResult<ReprioritizationValueOutput> {
  if (!Array.isArray(input?.fundedNpvUsd) || !Array.isArray(input?.displacedNpvUsd)) {
    return missing('fundedNpvUsd and displacedNpvUsd are required arrays.');
  }
  if (!input.fundedNpvUsd.every(isFiniteNumber) || !input.displacedNpvUsd.every(isFiniteNumber)) {
    return blocked('fundedNpvUsd and displacedNpvUsd must contain finite numbers.');
  }
  return {
    status: 'ok',
    output: Object.freeze({
      capitalFreedUsd: round(
        input.fundedNpvUsd.reduce((sum, value) => sum + value, 0) -
          input.displacedNpvUsd.reduce((sum, value) => sum + value, 0),
      ),
    }),
  };
}

type ConstrainedProject = Readonly<{ projectId: string; capexUsd: number; npvUsd: number; profitabilityIndex: number }>;
type ConstrainedSelectInput = Readonly<{ projects: readonly ConstrainedProject[]; budgetCapUsd: number }>;
type ConstrainedSelectOutput = Readonly<{
  method: 'greedy_pi';
  fundedProjectIds: readonly string[];
  displacedProjectIds: readonly string[];
  fundedCapexUsd: number;
  portfolioNpvUsd: number;
}>;

function executeConstrainedSelect(
  input: ConstrainedSelectInput,
): ApprovedFormulaResult<ConstrainedSelectOutput> {
  if (!Array.isArray(input?.projects) || !input.projects.length || !isFiniteNumber(input?.budgetCapUsd)) {
    return missing('projects and budgetCapUsd are required.');
  }
  if (input.budgetCapUsd <= 0) return blocked('budgetCapUsd must be greater than zero.');
  const seen = new Set<string>();
  const projects: ConstrainedProject[] = [];
  for (const value of input.projects) {
    if (!value || typeof value !== 'object' || !value.projectId || seen.has(value.projectId)) {
      return blocked('projects must carry unique projectId values.');
    }
    if (!isFiniteNumber(value.capexUsd) || value.capexUsd <= 0 || !isFiniteNumber(value.npvUsd) || !isFiniteNumber(value.profitabilityIndex)) {
      return blocked('Each project requires positive capexUsd and finite npvUsd and profitabilityIndex.');
    }
    seen.add(value.projectId);
    projects.push(value);
  }
  const funded: ConstrainedProject[] = [];
  let fundedCapexUsd = 0;
  for (const project of [...projects].sort((left, right) => right.profitabilityIndex - left.profitabilityIndex)) {
    if (fundedCapexUsd + project.capexUsd <= input.budgetCapUsd + 0.005) {
      funded.push(project);
      fundedCapexUsd += project.capexUsd;
    }
  }
  const fundedIds = new Set(funded.map((project) => project.projectId));
  return {
    status: 'ok',
    output: Object.freeze({
      method: 'greedy_pi',
      fundedProjectIds: Object.freeze(funded.map((project) => project.projectId)),
      displacedProjectIds: Object.freeze(projects.filter((project) => !fundedIds.has(project.projectId)).map((project) => project.projectId)),
      fundedCapexUsd: round(fundedCapexUsd),
      portfolioNpvUsd: round(funded.reduce((sum, project) => sum + project.npvUsd, 0)),
    }),
  };
}

type DeferKillInput = Readonly<{ projects: readonly Readonly<{ projectId: string; approvedCapexUsd: number; actualCapexUsd: number; strandedCostUsd: number }>[] }>;
type DeferKillOutput = Readonly<{ netCashUsd: number; excludedSunkActualCapexUsd: number }>;

function executeDeferKill(input: DeferKillInput): ApprovedFormulaResult<DeferKillOutput> {
  if (!Array.isArray(input?.projects) || !input.projects.length) return missing('projects are required.');
  let netCashUsd = 0;
  let excludedSunkActualCapexUsd = 0;
  const seen = new Set<string>();
  for (const project of input.projects) {
    if (!project || !project.projectId || seen.has(project.projectId)) return blocked('projects must carry unique projectId values.');
    if (![project.approvedCapexUsd, project.actualCapexUsd, project.strandedCostUsd].every((value) => isFiniteNumber(value) && value >= 0)) {
      return blocked('approvedCapexUsd, actualCapexUsd, and strandedCostUsd must be non-negative.');
    }
    seen.add(project.projectId);
    netCashUsd += Math.max(project.approvedCapexUsd - project.actualCapexUsd, 0) - project.strandedCostUsd;
    excludedSunkActualCapexUsd += project.actualCapexUsd;
  }
  return { status: 'ok', output: Object.freeze({ netCashUsd: round(netCashUsd), excludedSunkActualCapexUsd: round(excludedSunkActualCapexUsd) }) };
}

type EaaInput = Readonly<{ npvUsd: number; hurdlePct: number; assetLifeYears: number }>;
type EaaOutput = Readonly<{ equivalentAnnualAnnuityUsd: number }>;

function executeEaa(input: EaaInput): ApprovedFormulaResult<EaaOutput> {
  const hurdle = percentageFraction(input?.hurdlePct);
  if (!isFiniteNumber(input?.npvUsd) || hurdle === null || !isFiniteNumber(input?.assetLifeYears)) {
    return missing('npvUsd, hurdlePct, and assetLifeYears are required.');
  }
  if (input.assetLifeYears <= 0) return blocked('assetLifeYears must be greater than zero.');
  const annuityFactor = hurdle === 0
    ? input.assetLifeYears
    : (1 - (1 + hurdle) ** -input.assetLifeYears) / hurdle;
  return { status: 'ok', output: Object.freeze({ equivalentAnnualAnnuityUsd: round(input.npvUsd / annuityFactor) }) };
}

export const capitalAllocationFormulaDefinitions: readonly ApprovedFormulaDefinition[] = Object.freeze([
  definition({
    formulaId: 'capex_portfolio_rollup_v1',
    displayName: 'Capex portfolio rollup',
    description: 'Rolls approved project capex and expected return into a capital-cash-controlled portfolio total.',
    inputSchemaVersion: 'capex_portfolio_rollup_input_v1',
    outputSchemaVersion: 'capex_portfolio_rollup_output_v1',
    execute: executePortfolioRollup,
    goldenTraces: [Object.freeze({
      traceId: 'capex_portfolio_rollup:tieout:v1',
      input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 100, expectedReturnUsd: 140 }, { projectId: 'b', approvedCapexUsd: 200, expectedReturnUsd: 250 }]), capitalExpendituresUsd: 300 }),
      expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ totalProjectCapexUsd: 300, totalExpectedReturnUsd: 390, capitalExpendituresUsd: 300, residualUsd: 0, untaggedMaintenanceCapexUsd: 0 }) }),
    })],
  }),
  definition({
    formulaId: 'capex_roic_v1', displayName: 'Project ROIC versus WACC', description: 'Computes project ROIC, spread to the governed hurdle, and EVA.', inputSchemaVersion: 'capex_roic_input_v1', outputSchemaVersion: 'capex_roic_output_v1', execute: executeRoic,
    goldenTraces: [Object.freeze({ traceId: 'capex_roic:above-hurdle:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 100, nopatUsd: 20, investedCapitalUsd: 100 }]), hurdlePct: 10 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ projects: Object.freeze([{ projectId: 'a', roicPct: 20, hurdlePct: 10, spreadPct: 10, evaUsd: 10 }]) }) }) })],
  }),
  definition({
    formulaId: 'capex_npv_irr_v1', displayName: 'Project NPV, IRR, and MIRR', description: 'Computes NPV and IRR, withholding a single IRR for non-conventional cashflows while retaining MIRR.', inputSchemaVersion: 'capex_npv_irr_input_v1', outputSchemaVersion: 'capex_npv_irr_output_v1', execute: executeNpvIrr,
    goldenTraces: [Object.freeze({ traceId: 'capex_npv_irr:four-year-annuity:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 100, cashflowsUsd: Object.freeze([-100, 40, 40, 40, 40]) }]), hurdlePct: 10 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ projects: Object.freeze([{ projectId: 'a', npvUsd: 26.794618, irrPct: 21.86227, mirrPct: 16.726077, nonConventionalCashflow: false }]) }) }) })],
  }),
  definition({
    formulaId: 'capex_profitability_index_v1', displayName: 'Capex profitability index', description: 'Ranks projects by discounted value per capital dollar for capital-rationing decisions.', inputSchemaVersion: 'capex_profitability_index_input_v1', outputSchemaVersion: 'capex_profitability_index_output_v1', execute: executeProfitabilityIndex,
    goldenTraces: [Object.freeze({ traceId: 'capex_profitability_index:capital-rationing:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 100, cashflowsUsd: Object.freeze([-100, 110]) }]), hurdlePct: 10 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ projects: Object.freeze([{ projectId: 'a', npvUsd: 0, profitabilityIndex: 1 }]) }) }) })],
  }),
  definition({
    formulaId: 'capex_payback_v1', displayName: 'Capex payback', description: 'Computes undiscounted and discounted project payback periods as liquidity screens.', inputSchemaVersion: 'capex_payback_input_v1', outputSchemaVersion: 'capex_payback_output_v1', execute: executePayback,
    goldenTraces: [Object.freeze({ traceId: 'capex_payback:two-year:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 100, cashflowsUsd: Object.freeze([-100, 50, 50]) }]), hurdlePct: 0 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ projects: Object.freeze([{ projectId: 'a', paybackYears: 2, discountedPaybackYears: 2 }]) }) }) })],
  }),
  definition({
    formulaId: 'capex_mix_v1', displayName: 'Capex maintenance-growth mix', description: 'Computes the maintenance-growth capex split and capital intensity against profile bands.', inputSchemaVersion: 'capex_mix_input_v1', outputSchemaVersion: 'capex_mix_output_v1', execute: executeMix,
    goldenTraces: [Object.freeze({ traceId: 'capex_mix:balanced:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'm', approvedCapexUsd: 40, maintenanceGrowthTag: 'maintenance' as const }, { projectId: 'g', approvedCapexUsd: 60, maintenanceGrowthTag: 'growth' as const }]), revenueUsd: 1000, maintenanceTargetMinPct: 30, maintenanceTargetMaxPct: 50 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ totalCapexUsd: 100, maintenanceCapexUsd: 40, growthCapexUsd: 60, maintenanceRatioPct: 40, capexIntensityPct: 10, withinTargetBand: true }) }) })],
  }),
  definition({
    formulaId: 'capex_variance_v1', displayName: 'Capex budget variance', description: 'Computes actual-versus-approved capex variance by project.', inputSchemaVersion: 'capex_variance_input_v1', outputSchemaVersion: 'capex_variance_output_v1', execute: executeVariance,
    goldenTraces: [Object.freeze({ traceId: 'capex_variance:overrun:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 100, actualCapexUsd: 110 }]) }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ projects: Object.freeze([{ projectId: 'a', varianceUsd: 10, overrunPct: 10 }]) }) }) })],
  }),
  definition({
    formulaId: 'capex_reprioritization_value_v1', displayName: 'Capex reprioritization value', description: 'Computes cash-track value from funded less displaced project NPV.', inputSchemaVersion: 'capex_reprioritization_value_input_v1', outputSchemaVersion: 'capex_reprioritization_value_output_v1', execute: executeReprioritizationValue,
    goldenTraces: [Object.freeze({ traceId: 'capex_reprioritization_value:cash-track:v1', input: Object.freeze({ fundedNpvUsd: Object.freeze([100, 50]), displacedNpvUsd: Object.freeze([40, 10]) }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ capitalFreedUsd: 100 }) }) })],
  }),
  definition({
    formulaId: 'capex_constrained_select_v1', displayName: 'Constrained capex selection', description: 'Selects an explicitly labeled greedy-by-profitability-index project set under a capital cap.', inputSchemaVersion: 'capex_constrained_select_input_v1', outputSchemaVersion: 'capex_constrained_select_output_v1', execute: executeConstrainedSelect,
    goldenTraces: [Object.freeze({ traceId: 'capex_constrained_select:greedy-pi:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', capexUsd: 50, npvUsd: 20, profitabilityIndex: 1.4 }, { projectId: 'b', capexUsd: 60, npvUsd: 21, profitabilityIndex: 1.35 }]), budgetCapUsd: 60 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ method: 'greedy_pi' as const, fundedProjectIds: Object.freeze(['a']), displacedProjectIds: Object.freeze(['b']), fundedCapexUsd: 50, portfolioNpvUsd: 20 }) }) })],
  }),
  definition({
    formulaId: 'capex_defer_kill_v1', displayName: 'Capex defer-kill cash effect', description: 'Computes future capex freed net of stranded cost while excluding sunk actual capex.', inputSchemaVersion: 'capex_defer_kill_input_v1', outputSchemaVersion: 'capex_defer_kill_output_v1', execute: executeDeferKill,
    goldenTraces: [Object.freeze({ traceId: 'capex_defer_kill:exclude-sunk:v1', input: Object.freeze({ projects: Object.freeze([{ projectId: 'a', approvedCapexUsd: 130, actualCapexUsd: 30, strandedCostUsd: 20 }]) }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ netCashUsd: 80, excludedSunkActualCapexUsd: 30 }) }) })],
  }),
  definition({
    formulaId: 'capex_eaa_v1', displayName: 'Equivalent annual annuity', description: 'Annualizes NPV for unequal-life mutually exclusive asset decisions.', inputSchemaVersion: 'capex_eaa_input_v1', outputSchemaVersion: 'capex_eaa_output_v1', execute: executeEaa,
    goldenTraces: [Object.freeze({ traceId: 'capex_eaa:five-years:v1', input: Object.freeze({ npvUsd: 100, hurdlePct: 0, assetLifeYears: 5 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ equivalentAnnualAnnuityUsd: 20 }) }) })],
  }),
]);
